import { Spinner } from './feedback';
import { EditBoundary, EditOutletContext } from './EditBoundary';
import { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { api } from '../api/client';
import { freshCodeRequestKinds, noCodeRequestKinds } from '@mcpanel/core';
import type { ChangeRequest } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { formatDateTime, requestKindLabel, requestStatusInfo, viaLabel } from '../format';
import { Badge } from './ui';
import { Accordion } from './Accordion';

/** 変更リクエストの計画・警告と、承認・却下の操作。 */
function RequestReviewContent({
  request,
  onDecided,
  compact = false,
  expandPlan = false,
}: {
  request: ChangeRequest;
  onDecided: (request: ChangeRequest) => void;
  compact?: boolean;
  expandPlan?: boolean;
}) {
  const { canOperate, ensureSteppedUp, notify } = useApp();
  const [busy, setBusy] = useState(false);
  const status = requestStatusInfo[request.status] ?? { label: request.status, tone: 'muted' as const };
  const dangerous = request.warnings.some((w) => w.level === 'danger');
  const hasBefore = request.plan.changes.some((change) => change.before !== undefined);

  // 元に戻しやすい変更はワンタイムコードを求めない（API 側と同じ規則）
  const needsCode = !noCodeRequestKinds.includes(request.kind);
  const approve = async () => {
    setBusy(true);
    try {
      // 削除・復元は、5 分以内に入れたコードがあっても入れ直す
      if (needsCode) await ensureSteppedUp({ fresh: freshCodeRequestKinds.includes(request.kind) });
      const result = await api.approveRequest(request.id);
      notify('承認しました。ジョブを開始します。');
      onDecided(result);
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      onDecided(await api.rejectRequest(request.id));
      notify('却下しました');
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ gap: 12 }}>
      {!compact && (
        <div className="row">
          <Badge tone={status.tone}>{status.label}</Badge>
          <strong>{requestKindLabel[request.kind] ?? request.kind}</strong>
          {request.serverId && (
            <Link to={`/servers/${encodeURIComponent(request.serverId)}`} className="muted">
              {request.serverId}
            </Link>
          )}
          <span className="spacer" />
          <span className="small faint">
            {request.requestedBy}（{viaLabel[request.via] ?? request.via}）・{formatDateTime(request.createdAt)}
          </span>
        </div>
      )}
      <p style={{ margin: 0 }}>{request.plan.summary}</p>
      {(request.plan.changes.length > 0 || (request.plan.steps?.length ?? 0) > 0) && (
        <Accordion
          title="計画の詳細"
          summary={`${request.plan.changes.length} 項目 ・ ${request.plan.steps?.length ?? 0} 段階`}
          defaultOpen={request.status === 'pending' || expandPlan}
        >
          {request.plan.changes.length > 0 && (
            <div className="table-wrap card">
              <table>
                <thead>
                  <tr>
                    <th>項目</th>
                    {hasBefore && <th>変更前</th>}
                    <th>{hasBefore ? '変更後' : '内容'}</th>
                  </tr>
                </thead>
                <tbody>
                  {request.plan.changes.map((change) => (
                    <tr key={change.label} className={change.before === change.after ? 'unchanged' : undefined}>
                      <td className="small muted">{change.label}</td>
                      {hasBefore && <td className="muted">{change.before ?? '—'}</td>}
                      <td>
                        {change.before === change.after ? (
                          <span className="faint">変更なし</span>
                        ) : (
                          <strong>{change.after ?? '—'}</strong>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {request.plan.steps && request.plan.steps.length > 0 && (
            <ol className="small muted" style={{ margin: 0, paddingLeft: 20 }}>
              {request.plan.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </Accordion>
      )}
      {request.warnings.map((warning) => (
        <div key={warning.message} className={`alert ${warning.level === 'danger' ? 'danger' : 'warn'}`}>
          {warning.message}
        </div>
      ))}
      {request.via === 'mcp' && request.status === 'pending' && (
        <div className="alert info">AI からの依頼です。内容が意図どおりか確認してから承認してください。</div>
      )}
      {request.status === 'pending' && canOperate && (
        <div className="row">
          {request.plan.estimatedMinutes !== undefined && (
            <span className="small faint">所要の目安 約 {request.plan.estimatedMinutes} 分</span>
          )}
          <span className="spacer" />
          <span className="small faint">{formatDateTime(request.expiresAt)} まで有効</span>
          <button className="btn" disabled={busy} onClick={() => void reject()}>
            {busy && <Spinner />}
            却下
          </button>
          <button
            className={`btn ${dangerous ? 'danger-solid' : 'primary'}`}
            disabled={busy}
            onClick={() => void approve()}
          >
            {busy && <Spinner />}
            {needsCode ? 'ワンタイムコードで承認して実行' : '承認して実行'}
          </button>
        </div>
      )}
      {request.jobId && (
        <div className="small">
          <Link to={`/jobs?jobId=${encodeURIComponent(request.jobId)}`}>ジョブを確認する →</Link>
        </div>
      )}
    </div>
  );
}

export function RequestReview({
  automatic = true,
  ...props
}: Parameters<typeof RequestReviewContent>[0] & { automatic?: boolean }) {
  const { canOperate } = useApp();
  // 画面の編集ロックの中にあるときは、ロックの外（画面の下）に出す。編集ロックが閉じても承認できるように
  const outlet = useContext(EditOutletContext);
  const content =
    props.request.status !== 'pending' || !canOperate ? (
      <div className="stack">
        {props.request.status === 'pending' && (
          <div className="alert info">承認待ちになりました。承認できる人に知らせてください。</div>
        )}
        <RequestReviewContent {...props} />
      </div>
    ) : (
      <EditBoundary resource={{ target: `request:${props.request.id}`, screen: 'approval' }} automatic={automatic}>
        <RequestReviewContent {...props} />
      </EditBoundary>
    );
  return outlet ? createPortal(content, outlet) : content;
}
